import type { ImportRecord } from './fixtures';
import { withIssues, type PreviewItem, type PreviewOrder } from './order-preview-fixtures';

// 设计稿交互演示：仅保存本地草稿，真实交付替换为导入任务接口。
const version = 1;
const orderFields = ['name', 'mobile', 'campus', 'performanceCampus', 'owner', 'role', 'commission', 'note'] as const;
type DraftItem = Pick<PreviewItem, 'id' | 'project' | 'category' | 'quantity' | 'dueAmount' | 'expiresAt' | 'chargeType'>;
type DraftOrder = Pick<PreviewOrder, 'id' | (typeof orderFields)[number]> & { items: DraftItem[] };

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function editable(order: Pick<PreviewOrder, 'id' | (typeof orderFields)[number]> & { items: readonly DraftItem[] }): DraftOrder {
  return {
    id: order.id, name: order.name, mobile: order.mobile, campus: order.campus,
    performanceCampus: order.performanceCampus, owner: order.owner, role: order.role,
    commission: order.commission, note: order.note,
    items: order.items.map(({ id, project, category, quantity, dueAmount, expiresAt, chargeType }) => ({ id, project, category, quantity, dueAmount, expiresAt, chargeType })),
  };
}
function validOrder(value: unknown): value is DraftOrder {
  if (!object(value) || typeof value.id !== 'string' || !orderFields.every(field => typeof value[field] === 'string') || !Array.isArray(value.items)) return false;
  return value.items.every(item => object(item) && typeof item.id === 'string'
    && ['project', 'expiresAt', 'chargeType'].every(field => typeof item[field] === 'string')
    && (item.category === '课程' || item.category === '物品')
    && typeof item.quantity === 'number' && Number.isFinite(item.quantity)
    && typeof item.dueAmount === 'number' && Number.isFinite(item.dueAmount));
}
export function draftKeyForRecord(scope: string, record: ImportRecord): string {
  return `orderImport.draft.v1:${JSON.stringify([scope, record.id, record.fileName, record.uploadedAt])}`;
}
export function loadPreviewDraft(key: string, base: readonly PreviewOrder[]): { orders: readonly PreviewOrder[]; status: 'empty' | 'restored' | 'failed'; savedAt?: number } {
  try {
    const text = localStorage.getItem(key);
    if (text === null) return { orders: base, status: 'empty' };
    const data: unknown = JSON.parse(text);
    if (!object(data) || data.version !== version || data.count !== base.length || !Array.isArray(data.changes) || !data.changes.every(validOrder) || typeof data.savedAt !== 'number') throw new Error('Invalid draft');
    const changes = new Map<string, DraftOrder>(data.changes.map(order => [order.id, order]));
    const baseIds = new Set(base.map(order => order.id));
    if (changes.size !== data.changes.length || [...changes.keys()].some(id => !baseIds.has(id))) throw new Error('Incompatible draft');
    const orders = base.map(order => {
      const change = changes.get(order.id);
      if (!change) return order;
      if (change.items.length !== order.items.length || change.items.some((item, index) => item.id !== order.items[index].id)) throw new Error('Incompatible items');
      // 只覆盖可编辑字段；档案匹配值、标准价、原表行及校验结果来自当前基础数据。
      const safe = editable(change);
      return withIssues({ ...order, ...safe, items: order.items.map((item, index) => ({ ...item, ...safe.items[index] })) });
    });
    return { orders, status: 'restored', savedAt: data.savedAt };
  } catch {
    return { orders: base, status: 'failed' };
  }
}
export function savePreviewDraft(key: string, orders: readonly PreviewOrder[], base: readonly PreviewOrder[]): boolean {
  try {
    const changes = orders.flatMap((order, index) => {
      if (order === base[index]) return [];
      const draft = editable(order);
      return JSON.stringify(draft) === JSON.stringify(editable(base[index])) ? [] : [draft];
    });
    if (!changes.every(validOrder)) return false;
    localStorage.setItem(key, JSON.stringify({ version, count: base.length, changes, savedAt: Date.now() }));
    return true;
  } catch { return false; }
}
export function clearPreviewDraft(key: string): boolean {
  try { localStorage.removeItem(key); return true; } catch { return false; }
}
function recordsKey(scope: string) { return `orderImport.records.v1:${scope}`; }
export function loadImportRecords(scope: string, base: readonly ImportRecord[]): readonly ImportRecord[] {
  try {
    const text = localStorage.getItem(recordsKey(scope));
    if (text === null) return base;
    const data: unknown = JSON.parse(text);
    if (!object(data) || data.version !== version || !Array.isArray(data.records)) return base;
    const valid = data.records.every(record => object(record)
      && ['fileName', 'uploadedAt', 'operator'].every(field => typeof record[field] === 'string')
      && ['id', 'total', 'succeeded', 'failed'].every(field => typeof record[field] === 'number' && Number.isSafeInteger(record[field]) && (record[field] as number) >= 0)
      && typeof record.confirmed === 'boolean');
    if (!valid || new Set(data.records.map(record => record.id)).size !== data.records.length) return base;
    return data.records as ImportRecord[];
  } catch { return base; }
}
export function saveImportRecords(scope: string, records: readonly ImportRecord[]): boolean {
  try { localStorage.setItem(recordsKey(scope), JSON.stringify({ version, records })); return true; } catch { return false; }
}
